export type EnemyTypeId = "crosser" | "sprinter" | "caravan" | "splitter" | "kid" | "boss";
export type TowerTypeId = "watchtower" | "sniper" | "checkpoint" | "mortar" | "tesla" | "orbital";
export type PowerId = "drone" | "teargas";

export interface EnemySpec {
  id: EnemyTypeId;
  name: string;
  hp: number;
  speed: number;
  bounty: number;
  lifeCost: number;
  radius: number;
}

export interface TowerTier {
  name: string;
  cost: number;
  range: number;
  damage: number;
  fireRate: number;
  splash: number;
  slowFactor: number;
  targets?: number;
  pierce?: number;
  chain?: number;
  cluster?: number;
  global?: boolean;
  perk?: string;
}

export interface TowerSpec {
  id: TowerTypeId;
  label: string;
  icon: string;
  blurb: string;
  prestige?: boolean;
  tiers: readonly [TowerTier, TowerTier, TowerTier, TowerTier];
}

export interface PowerSpec {
  id: PowerId;
  label: string;
  icon: string;
  blurb: string;
  cooldown: number;
  radius: number;
  damage: number;
  slowFactor: number;
  slowDuration: number;
}

export interface SpawnGroup {
  type: EnemyTypeId;
  count: number;
  gap: number;
  delay: number;
}

export interface DefenseSave {
  version: 1;
  bestScore: number;
  bestWave: number;
}

export interface RandomSource {
  next(): number;
}

export const TILE = 64;
export const COLS = 20;
export const ROWS = 12;
export const WIDTH = COLS * TILE;
export const HEIGHT = ROWS * TILE;
export const START_CASH = 220;
export const START_LIVES = 20;
export const REFUND_RATE = 0.7;
export const BOSS_EVERY = 10;
export const FIXED_STEP = 1 / 60;
export const FIRST_WAVE_DELAY = 2.5;
export const BETWEEN_WAVE_GRACE = 9;

export const WAYPOINTS: readonly (readonly [number, number])[] = [
  [0, 1],
  [17, 1],
  [17, 3],
  [2, 3],
  [2, 5],
  [17, 5],
  [17, 7],
  [2, 7],
  [2, 9],
  [17, 9],
  [17, 11],
  [19, 11],
];

function buildPathTiles(): Set<number> {
  const tiles = new Set<number>();
  for (let index = 0; index < WAYPOINTS.length - 1; index += 1) {
    const from = WAYPOINTS[index];
    const to = WAYPOINTS[index + 1];
    if (!from || !to) continue;
    const [fromCol, fromRow] = from;
    const [toCol, toRow] = to;
    const stepCol = Math.sign(toCol - fromCol);
    const stepRow = Math.sign(toRow - fromRow);
    let col = fromCol;
    let row = fromRow;
    tiles.add(row * COLS + col);
    while (col !== toCol || row !== toRow) {
      col += stepCol;
      row += stepRow;
      tiles.add(row * COLS + col);
    }
  }
  return tiles;
}

export const PATH_TILES: ReadonlySet<number> = buildPathTiles();

export function isPathTile(col: number, row: number): boolean {
  return PATH_TILES.has(row * COLS + col);
}

export function isBuildable(col: number, row: number): boolean {
  return col >= 0 && col < COLS && row >= 0 && row < ROWS && !isPathTile(col, row);
}

export const PATH_POINTS: readonly (readonly [number, number])[] = [
  [-TILE * 1.5, TILE * 1.5],
  ...WAYPOINTS.map(([col, row]) => [(col + 0.5) * TILE, (row + 0.5) * TILE] as const),
  [WIDTH + TILE * 1.5, TILE * 11.5],
];

export const ENEMY_SPECS: Record<EnemyTypeId, EnemySpec> = {
  crosser: { id: "crosser", name: "Crosser", hp: 26, speed: 85, bounty: 6, lifeCost: 1, radius: 11 },
  sprinter: { id: "sprinter", name: "Visa Runner", hp: 15, speed: 165, bounty: 9, lifeCost: 1, radius: 9 },
  caravan: { id: "caravan", name: "Caravan", hp: 140, speed: 45, bounty: 22, lifeCost: 2, radius: 16 },
  splitter: { id: "splitter", name: "Anchor Family", hp: 60, speed: 65, bounty: 14, lifeCost: 1, radius: 13 },
  kid: { id: "kid", name: "Anchor Kid", hp: 12, speed: 120, bounty: 3, lifeCost: 1, radius: 7 },
  boss: { id: "boss", name: "Coyote Bus", hp: 950, speed: 35, bounty: 160, lifeCost: 5, radius: 24 },
};

export const TOWER_SPECS: Record<TowerTypeId, TowerSpec> = {
  watchtower: {
    id: "watchtower",
    label: "Watchtower",
    icon: "🗼",
    blurb: "Cheap, rapid fire, short range",
    tiers: [
      { name: "Watchtower", cost: 50, range: 170, damage: 7, fireRate: 2.4, splash: 0, slowFactor: 1 },
      { name: "Guard Post", cost: 60, range: 185, damage: 12, fireRate: 2.8, splash: 0, slowFactor: 1 },
      { name: "Gun Nest", cost: 150, range: 200, damage: 16, fireRate: 3.2, splash: 0, slowFactor: 1, targets: 2, perk: "Twin guns — hits 2 targets at once" },
      { name: "Minigun Bunker", cost: 480, range: 215, damage: 22, fireRate: 4.6, splash: 0, slowFactor: 1, targets: 3, perk: "Minigun — shreds 3 targets at once" },
    ],
  },
  sniper: {
    id: "sniper",
    label: "Sniper Nest",
    icon: "🎯",
    blurb: "Long range, slow, heavy damage",
    tiers: [
      { name: "Sniper Nest", cost: 120, range: 350, damage: 48, fireRate: 0.55, splash: 0, slowFactor: 1 },
      { name: "Marksman Perch", cost: 170, range: 390, damage: 100, fireRate: 0.62, splash: 0, slowFactor: 1 },
      { name: "Deadeye Post", cost: 420, range: 430, damage: 190, fireRate: 0.7, splash: 0, slowFactor: 1, pierce: 3, perk: "Piercing rounds — hits 3 crossers in a line" },
      { name: "Railgun Roost", cost: 950, range: 470, damage: 400, fireRate: 0.85, splash: 0, slowFactor: 1, pierce: 6, perk: "Railgun slugs — pierce clean through 6 crossers" },
    ],
  },
  checkpoint: {
    id: "checkpoint",
    label: "Checkpoint",
    icon: "🚧",
    blurb: "Slows everything in radius",
    tiers: [
      { name: "Checkpoint", cost: 80, range: 150, damage: 1, fireRate: 1, splash: 0, slowFactor: 0.65 },
      { name: "Processing Center", cost: 110, range: 170, damage: 3, fireRate: 1, splash: 0, slowFactor: 0.5 },
      { name: "Detention Hub", cost: 280, range: 195, damage: 8, fireRate: 1.5, splash: 0, slowFactor: 0.38 },
      { name: "Deportation Complex", cost: 650, range: 225, damage: 16, fireRate: 2, splash: 0, slowFactor: 0.25, perk: "Maximum security — crossers crawl at 25% speed" },
    ],
  },
  mortar: {
    id: "mortar",
    label: "Mortar Pit",
    icon: "💥",
    blurb: "Splash damage, slow lob",
    tiers: [
      { name: "Mortar Pit", cost: 150, range: 235, damage: 30, fireRate: 0.5, splash: 75, slowFactor: 1 },
      { name: "Artillery Nest", cost: 200, range: 250, damage: 60, fireRate: 0.55, splash: 90, slowFactor: 1 },
      { name: "Freedom Launcher", cost: 480, range: 270, damage: 120, fireRate: 0.6, splash: 115, slowFactor: 1 },
      { name: "Cluster Battery", cost: 1200, range: 290, damage: 150, fireRate: 0.65, splash: 120, slowFactor: 1, cluster: 3, perk: "Cluster shells — splits into 3 sub-bombs on impact" },
    ],
  },
  tesla: {
    id: "tesla",
    label: "Shock Fence",
    icon: "⚡",
    blurb: "PRESTIGE — chain lightning that arcs between crossers",
    prestige: true,
    tiers: [
      { name: "Electric Fence", cost: 1800, range: 190, damage: 80, fireRate: 1.1, splash: 0, slowFactor: 1, chain: 3 },
      { name: "High Voltage", cost: 2800, range: 205, damage: 120, fireRate: 1.2, splash: 0, slowFactor: 1, chain: 4 },
      { name: "Power Grid", cost: 4200, range: 220, damage: 175, fireRate: 1.35, splash: 0, slowFactor: 1, chain: 5 },
      { name: "Rolling Blackout", cost: 7500, range: 240, damage: 260, fireRate: 1.55, splash: 0, slowFactor: 1, chain: 7, perk: "Full blackout — lightning chains 7 crossers deep" },
    ],
  },
  orbital: {
    id: "orbital",
    label: "Freedom Star",
    icon: "🛰️",
    blurb: "PRESTIGE — orbital strike on the toughest target, anywhere",
    prestige: true,
    tiers: [
      { name: "Freedom Star", cost: 2500, range: 9999, damage: 350, fireRate: 0.4, splash: 90, slowFactor: 1, global: true },
      { name: "Orbital Cannon", cost: 4000, range: 9999, damage: 650, fireRate: 0.45, splash: 105, slowFactor: 1, global: true },
      { name: "Sky Anvil", cost: 6500, range: 9999, damage: 1100, fireRate: 0.5, splash: 120, slowFactor: 1, global: true },
      { name: "Rods from God", cost: 10000, range: 9999, damage: 2000, fireRate: 0.55, splash: 145, slowFactor: 1, global: true, perk: "Kinetic bombardment — deletes whatever it looks at" },
    ],
  },
};

export const POWER_SPECS: Record<PowerId, PowerSpec> = {
  drone: {
    id: "drone",
    label: "Drone Strike",
    icon: "🛩️",
    blurb: "Targeted airstrike, big AoE damage",
    cooldown: 24,
    radius: 105,
    damage: 240,
    slowFactor: 1,
    slowDuration: 0,
  },
  teargas: {
    id: "teargas",
    label: "Tear Gas",
    icon: "🧯",
    blurb: "Gas cloud slows everyone inside",
    cooldown: 18,
    radius: 115,
    damage: 0,
    slowFactor: 0.35,
    slowDuration: 5,
  },
};

export const KILL_QUIPS: readonly string[] = [
  "VISA DENIED!",
  "BACK TO SENDER!",
  "DEPORTED!",
  "ENTRY REFUSED!",
  "PAPERWORK ERROR!",
  "RETURN TO SENDER!",
  "APPLICATION REJECTED!",
  "NO STAMP FOR YOU!",
];

export class SeededRandom {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0 || 0x6d2b79f5;
  }

  next(): number {
    let value = this.state;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.state = value >>> 0;
    return this.state / 0x1_0000_0000;
  }
}

export function randomSeed(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] ?? 1;
}

export function isBossWave(wave: number): boolean {
  return wave > 0 && wave % BOSS_EVERY === 0;
}

export function hpScale(wave: number): number {
  const early = 1 + Math.max(0, wave - 1) * 0.13;
  const late = 1 + Math.max(0, wave - 10) * 0.06;
  const endless = 1 + Math.max(0, wave - 18) * 0.12;
  return early * late * endless;
}

export function speedScale(wave: number): number {
  return 1 + Math.min(0.45, Math.max(0, wave - 22) * 0.015);
}

export function waveBonus(wave: number): number {
  return 40 + Math.max(0, wave) * 10 + Math.max(0, wave - 15) * 30;
}

export function comboMultiplier(streak: number): number {
  return Math.min(5, 1 + Math.floor(Math.max(0, streak) / 8));
}

export function bountyFor(spec: EnemySpec, streak: number, wave = 1): number {
  const waveFactor = 1 + Math.max(0, wave - 12) * 0.06;
  return Math.round(spec.bounty * comboMultiplier(streak) * waveFactor);
}

export function investedIn(spec: TowerSpec, tier: number): number {
  let total = 0;
  for (let index = 0; index <= tier && index < spec.tiers.length; index += 1) {
    total += spec.tiers[index]?.cost ?? 0;
  }
  return total;
}

export function refundFor(spec: TowerSpec, tier: number): number {
  return Math.floor(investedIn(spec, tier) * REFUND_RATE);
}

export function upgradeCost(spec: TowerSpec, tier: number): number | null {
  const next = spec.tiers[tier + 1];
  return next ? next.cost : null;
}

export function waveComposition(wave: number): SpawnGroup[] {
  if (wave <= 0) return [];
  switch (wave) {
    case 1:
      return [{ type: "crosser", count: 6, gap: 1.15, delay: 0 }];
    case 2:
      return [{ type: "crosser", count: 9, gap: 1.0, delay: 0 }];
    case 3:
      return [
        { type: "crosser", count: 8, gap: 0.95, delay: 0 },
        { type: "sprinter", count: 4, gap: 0.7, delay: 4 },
      ];
    case 4:
      return [
        { type: "crosser", count: 12, gap: 0.85, delay: 0 },
        { type: "sprinter", count: 6, gap: 0.6, delay: 5 },
      ];
    case 5:
      return [
        { type: "caravan", count: 1, gap: 1, delay: 2 },
        { type: "crosser", count: 10, gap: 0.8, delay: 0 },
      ];
    case 6:
      return [
        { type: "crosser", count: 14, gap: 0.75, delay: 0 },
        { type: "sprinter", count: 8, gap: 0.55, delay: 4 },
      ];
    case 7:
      return [
        { type: "splitter", count: 6, gap: 1.2, delay: 0 },
        { type: "crosser", count: 8, gap: 0.7, delay: 3 },
      ];
    case 8:
      return [
        { type: "caravan", count: 2, gap: 4, delay: 0 },
        { type: "sprinter", count: 10, gap: 0.5, delay: 2 },
      ];
    case 9:
      return [
        { type: "splitter", count: 8, gap: 1.0, delay: 0 },
        { type: "crosser", count: 12, gap: 0.6, delay: 4 },
      ];
    default:
      return scaledWave(wave);
  }
}

function scaledWave(wave: number): SpawnGroup[] {
  const random = new SeededRandom(wave * 0x9e3779b1);
  const groups: SpawnGroup[] = [];
  if (isBossWave(wave)) {
    const bosses = 1 + Math.floor(wave / BOSS_EVERY / 3);
    groups.push({ type: "boss", count: bosses, gap: 8, delay: 1 });
    groups.push({ type: "caravan", count: Math.floor(wave / 5), gap: 2.4, delay: 3 });
    groups.push({ type: "sprinter", count: 6 + wave, gap: 0.5, delay: 5 });
    return groups;
  }
  const crossers = 10 + Math.floor(wave * 1.2);
  const sprinters = 4 + Math.floor(wave * 0.7);
  const caravans = Math.floor((wave - 3) / 3);
  const splitters = Math.floor((wave - 4) / 2);
  groups.push({ type: "crosser", count: crossers, gap: Math.max(0.35, 0.8 - wave * 0.015), delay: 0 });
  if (sprinters > 0) groups.push({ type: "sprinter", count: sprinters, gap: 0.5, delay: 2 + random.next() * 3 });
  if (caravans > 0) groups.push({ type: "caravan", count: caravans, gap: 3, delay: 1 });
  if (splitters > 0) groups.push({ type: "splitter", count: splitters, gap: 1.1, delay: 4 });
  return groups;
}

export function pickQuip(random: RandomSource): string {
  const index = Math.floor(random.next() * KILL_QUIPS.length);
  return KILL_QUIPS[index] ?? "DEPORTED!";
}

const DEFAULT_SAVE: DefenseSave = { version: 1, bestScore: 0, bestWave: 0 };

export function parseDefenseSave(value: string | null): DefenseSave {
  if (!value) return { ...DEFAULT_SAVE };
  try {
    const parsed = JSON.parse(value) as Partial<DefenseSave>;
    return {
      version: 1,
      bestScore: finiteNonNegative(parsed.bestScore),
      bestWave: finiteNonNegative(parsed.bestWave),
    };
  } catch {
    return { ...DEFAULT_SAVE };
  }
}

function finiteNonNegative(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}
